import type { ApiErrorBody } from '@setone-sounds/shared/types';

export class ApiClientError extends Error {
  readonly code: string;
  readonly status: number;
  readonly body: ApiErrorBody | null;

  constructor(message: string, opts: { code: string; status: number; body?: ApiErrorBody | null }) {
    super(message);
    this.name = 'ApiClientError';
    this.code = opts.code;
    this.status = opts.status;
    this.body = opts.body ?? null;
  }
}

function isApiErrorEnvelope(value: unknown): value is { error: ApiErrorBody } {
  if (!value || typeof value !== 'object') return false;
  const err = (value as { error?: unknown }).error;
  if (!err || typeof err !== 'object') return false;
  const body = err as ApiErrorBody;
  return typeof body.code === 'string' && typeof body.message === 'string';
}

export type ApiFetchOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  /** Skip JSON parse (e.g. empty 204). */
  raw?: boolean;
};

export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { body, headers, raw, ...rest } = options;
  const init: RequestInit = {
    credentials: 'include',
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
  };

  if (body !== undefined) {
    init.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  const res = await fetch(path, init);
  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');

  let parsed: unknown = null;
  if (!raw && isJson) {
    try {
      parsed = await res.json();
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    if (isApiErrorEnvelope(parsed)) {
      throw new ApiClientError(parsed.error.message, {
        code: parsed.error.code,
        status: res.status,
        body: parsed.error,
      });
    }
    throw new ApiClientError(res.statusText || 'Request failed', {
      code: 'HTTP_ERROR',
      status: res.status,
    });
  }

  if (raw) {
    return undefined as T;
  }

  return parsed as T;
}
